import { useMemo } from "react";
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  ACCURACY_BAR_COLORS,
  ACCURACY_BAR_LEGEND,
  computeAccuracyTrend,
  DailyReading,
  getAccuracyBarColor,
} from "../../constants/dashboardStats";
import "./AccuracyChart.scss";

type AccuracyChartProps = {
  data: DailyReading[];
};

type ChartRow = DailyReading & {
  trend: number | null;
  barValue: number;
};

// Rendered bar height (not the value shown in the tooltip).
const ZERO_ACCURACY_BAR_HEIGHT = 2;

const getAccuracyBarFill = (row: DailyReading): string => {
  if (row.questions === 0) return "transparent";
  // Practiced but scored 0% — show as "needs work" red, not a gap.
  if (row.accuracy === 0) return ACCURACY_BAR_COLORS.weak;
  return getAccuracyBarColor(row.accuracy);
};

const getAccuracyBarValue = (row: DailyReading): number => {
  if (row.questions === 0) return 0;
  // Recharts draws zero-height bars as invisible; nudge to a sliver so 0% days are visible.
  if (row.accuracy === 0) return ZERO_ACCURACY_BAR_HEIGHT;
  return row.accuracy;
};

type TooltipContentProps = {
  active?: boolean;
  payload?: Array<{ payload: ChartRow }>;
  label?: string;
};

const ChartTooltip = ({ active, payload, label }: TooltipContentProps) => {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;

  return (
    <div className="accuracy-chart__tooltip">
      <p className="accuracy-chart__tooltip-title">{label}</p>
      <p className="accuracy-chart__tooltip-row">
        {row.questions === 0 ? "No questions done" : `Average correctness: ${row.accuracy}%`}
      </p>
    </div>
  );
};

const AccuracyChart = ({ data }: AccuracyChartProps) => {
  const chartData = useMemo(() => {
    const trend = computeAccuracyTrend(data.map((row) => row.accuracy));
    return data.map((row, index) => ({
      ...row,
      trend: trend[index],
      barValue: getAccuracyBarValue(row),
    }));
  }, [data]);

  return (
    <div className="accuracy-chart">
      <p className="accuracy-chart__description">
        Each bar shows your <strong>average correctness</strong> on a day you practiced.
        The trend line tracks how that changes over time — an <strong>upward slope means you&apos;re
        learning</strong> and answering more questions correctly.
      </p>
      <div className="accuracy-chart__legend" aria-label="Score colour key">
        {ACCURACY_BAR_LEGEND.map((item) => (
          <span className="accuracy-chart__legend-item" key={item.label}>
            <span
              className="accuracy-chart__legend-swatch"
              style={{ background: item.color }}
            />
            <span>
              {item.label}
              <span className="accuracy-chart__legend-caption"> · {item.caption}</span>
            </span>
          </span>
        ))}
        <span className="accuracy-chart__legend-item">
          <span className="accuracy-chart__legend-swatch accuracy-chart__legend-swatch--empty" />
          No practice
        </span>
        <span className="accuracy-chart__legend-item">
          <span className="accuracy-chart__legend-swatch accuracy-chart__legend-swatch--trend" />
          <span>
            Trend line
            <span className="accuracy-chart__legend-caption"> · rising = improving</span>
          </span>
        </span>
      </div>
      <ResponsiveContainer width="100%" height={220}>
        <ComposedChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid stroke="rgba(150,150,150,0.1)" vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 15, fill: "rgba(17, 24, 39, 0.55)" }}
            tickLine={false}
            axisLine={false}
            interval={data.length > 15 ? Math.floor(data.length / 8) : 0}
            angle={data.length > 7 ? -45 : 0}
            textAnchor={data.length > 7 ? "end" : "middle"}
            height={data.length > 7 ? 50 : 30}
          />
          <YAxis
            tick={{ fontSize: 15, fill: "rgba(17, 24, 39, 0.55)" }}
            tickLine={false}
            axisLine={false}
            width={40}
            domain={[0, 100]}
            tickFormatter={(value) => `${value}%`}
          />
          <Tooltip
            isAnimationActive={false}
            content={<ChartTooltip />}
            cursor={{ fill: "rgba(27, 94, 166, 0.06)" }}
          />
          <Bar dataKey="barValue" radius={[3, 3, 0, 0]} maxBarSize={data.length <= 7 ? 36 : 14}>
            {chartData.map((row) => (
              <Cell key={row.date} fill={getAccuracyBarFill(row)} />
            ))}
          </Bar>
          <Line
            type="linear"
            dataKey="trend"
            stroke={ACCURACY_BAR_COLORS.trend}
            strokeWidth={2}
            dot={false}
            connectNulls
            isAnimationActive={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

export default AccuracyChart;
